<template>
  <header
    class="header header_default"
    :class="[
      menuOpened && 'header_opened',
      state === 'authorized' && backText && 'header_back',
    ]"
  >
    <div class="header__row">
      <div class="header__group">
        <div class="header__item header__logo">
          <a href="/"><img :src="logo" :alt="logoAlt"></a>
        </div>
        <div class="header__item header__back">
          <Button
            variant="clear"
            size="lg"
            iconLeft="left"
            mobile
            @click="onBack"
          >
          </Button>
          <span class="h3" v-html="backText || ''"></span>
        </div>
      </div>
      <div v-if="state === 'unauthorized'" class="header__group ml-auto">
        <div class="header__item">
          <HeaderMenu
            :menu="menu"
            :menuHidden="menuHidden"
          />
        </div>
      </div>
      <div v-if="state === 'unauthorized'" class="header__group">
        <div class="header__item">
          <nav class="header-nav p-0">
            <button class="header-account" @click.stop="onProfile">
              <Avatar
                class="header-account__avatar"
                :img="profile.avatarUrl || ''"
                :size="40"
                bordered
              >
              </Avatar>
            </button>
          </nav>
        </div>
        <div class="header__item">
          <nav class="header-nav p-0 d-md-none">
            <Button
              class="header-nav__item"
              variant="clear"
              icon-left="menu"
              shape="circle"
              size="lg"
              mobile
              @click="onOpenMenu"
            ></Button>
          </nav>
        </div>
      </div>
      <div v-if="state === 'authorized'" class="header__group ml-auto">
        <div class="header__item header__item_profile">
          <Avatar
            class="header-account__avatar"
            :img="profile.avatarUrl || ''"
            :size="40"
            bordered
          >
          </Avatar>
          <div class="header-account__name">{{ profile.name || '' }}</div>
        </div>
        <div class="header__item">
          <nav class="header-nav p-0">
            <Button
              class="header-nav__item header__notification-button"
              variant="clear"
              size="lg"
              mobile
              @click.stop="onNotifications"
            >
              <lottie-player
                :src="notificationAnim"
                style="width: 24px;"
                hover
              ></lottie-player>
            </Button>
          </nav>
        </div>
        <div class="header__item">
          <nav class="header-nav p-0">
            <Button
              class="header-nav__item header__wallet-button"
              variant="clear"
              size="lg"
              mobile
              @click="onOpenWallet"
            >
              <img :src="coinImage" class="header__wallet-image" />
              <span class="h3">{{coins || 0}}</span>
            </Button>
          </nav>
        </div>
      </div>
    </div>
    <div v-if="state === 'unauthorized'" class="header__overlay" @click="onCloseMenu"></div>
    <!-- Mobile menu : BEGIN -->
    <div v-if="state === 'unauthorized'" class="menu" @click="onCloseMenu">
      <div class="menu__window">
        <div class="menu__header">
          <div
            class="menu__logo"
          >
            <a href="/"><img :src="logo" :alt="logoAlt"></a>
          </div>
          <Button
            variant="clear"
            icon-left="close"
            size="lg"
            shape="square"
            mobile
            @click="onCloseMenu"
          >
          </Button>
        </div>
        <div class="menu__body">
          <div
            class="menu__item"
            v-for="(item, i) in mobileMenu"
            :key="i"
            v-show="!item.hidden"
          >
            <div class="divider" v-if="item.divider"></div>
            <template v-else>
              <a
                :href="typeof item.action === 'function' ? '#' : item.action"
                :target="item.target ? item.target : ''"
                class="menu__link h3"
                @click="onNavClick($event, item)"
              >
                <span v-html="item.text"></span>
                <Icon v-if="item.external === true" class="link_external" name="externa-link" size="l"></Icon>
              </a>
              <div class="menu__subnav" v-if="item.nav">
                <a
                  :href="typeof subitem.action === 'function' ? '#' : subitem.action"
                  :target="subitem.target ? subitem.target : ''"
                  class="menu__sublink"
                  v-for="(subitem, j) in item.nav"
                  :key="j"
                  @click="onNavClick($event, subitem)"
                  v-show="!subitem.hidden"
                >
                  <Icon class="menu__sublink-icon" :name="subitem.icon" size="l"></Icon>
                  <span class="menu__sublink-text">
                    {{ subitem.text }}
                  </span>
                </a>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
    <!-- Mobile menu : END -->
  </header>
</template>

<script>
import Button from "@rr-component-library/button/src/main";
import Icon from "@rr-component-library/icon/src/main";
import Avatar from './Avatar.vue';
import HeaderMenu from './HeaderMenu.vue';
import NotificationAnim from '../assets/img/notification.json';
import CoinImage from '../assets/img/coin.svg';
import * as LottiePlayer from "@lottiefiles/lottie-player";

export default {
  name: 'Header',
  designVersion: '1.0.1',
  components: {
    Button,
    Icon,
    Avatar,
    HeaderMenu,
  },
  props: {
    /**
     * Файл логотипа
     */
    logo: {
      type: String,
    },
    /**
     * Alt-текст для логотипа
     */
    logoAlt: {
      type: String,
    },
    /**
     * Данные профиля пользователя
     */
    profile: {
      type: Object,
    },
    /**
     * Пункты главного меню
     */
    menu: {
      type: Array,
    },
    /**
     * "Скрытые" по-умолчанию пункты главного меню
     */
    menuHidden: {
      type: Array,
    },
    /**
     * Пункты мобильного меню
     */
    mobileMenu: {
      type: Array,
    },
    /**
     * Состояние:<br>
     * `'unauthorized'` - пользователь неавторизован<br>
     * `'authorized'` - пользователь авторизован
     */
    state: {
      type: String,
      default: 'unauthorized',
      validator(value) {
        return ['', 'unauthorized', 'authorized'].indexOf(value) !== -1;
      },
    },
    /**
     * Массив уведомлений
     */
    notifications: {
      type: Array,
    },
    /**
     * Количество монет на счете
     */
    coins: {
      type: Number,
    },
    /**
     * Текст ссылки возврата
     */
    backText: {
      type: String,
    },
  },
  data() {
    return {
      menuOpened: false,
      notificationAnim: JSON.stringify(NotificationAnim),
      coinImage: CoinImage,
    };
  },
  methods: {
    onCloseMenu() {
      this.menuOpened = false;
    },
    onOpenMenu() {
      this.menuOpened = true;
    },
    onProfile() {
      if (this.profile.link) {
        window.location.href = this.profile.link;
      }
    },
    onNotifications() {
      this.$emit('open-notifications');
    },
    onOpenWallet() {
      this.$emit('open-wallet');
    },
    onNavClick(e, item) {
      if (item.action) {
        if (typeof item.action === 'function') {
          e.preventDefault();
          item.action();
        }
      }
    },
    onBack() {
      this.$emit('back');
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "./Header";
</style>

